import React from 'react';
import { motion } from 'framer-motion';
import { 
  Code2, 
  Globe, 
  Smartphone, 
  Layers, 
  Cpu, 
  ShieldCheck, 
  ArrowRight,
  CheckCircle,
  Lock,
  MessageSquare,
  PhoneCall
} from 'lucide-react';
import './CustomSoftwareDev.css';
import FAQAccordion from './FAQAccordion';

const services = [
  {
    icon: <Globe size={24} />,
    title: 'Custom Web Application Development',
    desc: 'High-performance web apps, enterprise portals, customer dashboards, and SaaS platforms built using React, Next.js, and Node.js microservices.'
  },
  {
    icon: <Smartphone size={24} />,
    title: 'Android & iOS Mobile Apps',
    desc: 'Cross-platform Flutter & React Native apps plus native iOS (Swift) and Android (Kotlin) development with seamless API integration and 60 FPS UI.'
  },
  {
    icon: <Layers size={24} />,
    title: 'Custom Enterprise ERP & CRM',
    desc: 'Bespoke ERPs for manufacturing, foundries & steel mills, plus sales CRMs with automated WhatsApp API messaging, IndiaMart sync, and Tally ledgers.'
  },
  {
    icon: <Cpu size={24} />,
    title: 'API & Legacy System Middleware',
    desc: 'Connect existing databases, Tally ERP, payment gateways, IoT sensors, and third-party SaaS tools with secure REST & GraphQL APIs.'
  },
  {
    icon: <Lock size={24} />,
    title: 'Cybersecurity & Compliance Audit',
    desc: 'ISO 27001, DPDP Act 2025, and HIPAA data compliance checks, penetration testing, zero-trust network setups, and vulnerability patching.'
  },
  {
    icon: <ShieldCheck size={24} />,
    title: '24/7 AMC & Maintenance Support',
    desc: 'Dedicated Annual Maintenance Contracts (AMC), SLA-backed emergency support, cloud infra cost optimization, and feature enhancements.'
  }
];

const techStack = [
  { category: 'Frontend', tech: ['React.js', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Redux / Zustand'] },
  { category: 'Backend & APIs', tech: ['Node.js', 'Express', 'Python / Django', 'Java / Spring Boot', 'GraphQL / REST'] },
  { category: 'Mobile Apps', tech: ['Flutter', 'React Native', 'Swift (iOS)', 'Kotlin (Android)'] },
  { category: 'Databases & Cloud', tech: ['PostgreSQL', 'MongoDB', 'Redis', 'AWS', 'Docker', 'Kubernetes'] }
];

const processStepsDetailed = [
  { num: '01', title: 'Free Discovery & Requirement Mapping', desc: 'We meet (in-person in Delhi NCR or virtually) to analyze your business workflows, define technical requirements, and provide a transparent project proposal.' },
  { num: '02', title: 'Architecture & Figma Wireframing', desc: 'Designing interactive Figma prototypes, UI/UX screens, and database schemas before writing a single line of code.' },
  { num: '03', title: 'Agile Sprint Development', desc: 'Iterative 2-week development sprints with weekly progress updates, staging site previews, and continuous feedback.' },
  { num: '04', title: 'Security Audit & Penetration QA', desc: 'Rigorous automated testing, security penetration tests, multi-device QA, and vulnerability patching.' },
  { num: '05', title: 'Production Launch & Deployment', desc: 'Zero-downtime cloud production launch on AWS/Railway, SSL installation, and complete 100% source code handover.' },
  { num: '06', title: 'Staff Training & 24/7 AMC Support', desc: 'On-site/virtual staff training, 30 days of free post-launch support, and dedicated SLA-backed Annual Maintenance Contracts (AMC).' }
];

const faqsDetailed = [
  { q: 'How is custom software development done step-by-step at Wayug Infotech?', a: 'Our development journey follows 6 structured stages: (1) Free Discovery & Scope Mapping, (2) Architecture & Figma Wireframing, (3) Agile 2-Week Development Sprints, (4) Security Audit & Penetration QA, (5) Production Launch & Source Code Handover, and (6) Staff Training & 24/7 AMC Support.' },
  { q: 'Do we get full 100% source code ownership?', a: 'Yes! Upon project completion and final milestone clearance, 100% of the source code, intellectual property rights, repository access, and documentation are transferred entirely to your company.' },
  { q: 'Do you sign Non-Disclosure Agreements (NDAs)?', a: 'Absolutely. We sign strict NDAs before reviewing any proprietary business ideas, workflows, or project specifications to ensure total confidentiality.' },
  { q: 'What is your payment milestone structure?', a: 'We work on transparent, milestone-based payments (e.g., 25% project kickoff, 25% UI/prototype approval, 25% beta testing, 25% final launch).' },
  { q: 'How long does a typical custom software project take?', a: 'Standard web applications and mobile apps take 6 to 12 weeks from initial discovery to live production launch depending on feature complexity.' },
  { q: 'Do you provide ongoing support after project launch?', a: 'Yes! All projects include 30 days of free post-launch support. After that, we offer optional SLA-backed Annual Maintenance Contracts (AMC) for feature updates and 24/7 monitoring.' },
  { q: 'Can you integrate our custom software with Tally or legacy databases?', a: 'Yes! We build secure REST middleware to sync financial ledgers, inventory, and invoices directly with Tally ERP, SQL Server, Oracle, or legacy systems.' }
];

export default function CustomSoftwareDev() {
  const whatsappUrl = "https://wa.me/918287999236?text=Hi%20Wayug!%20I%20have%20a%20custom%20software%20requirement%20and%20would%20like%20to%20discuss.";

  return (
    <section className="csd-section">
      <div className="container">
        
        {/* Hero */}
        <motion.div 
          className="csd-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="csd-badge">
            <Code2 size={16} /> End-to-End Software Engineering
          </div>
          <h1 className="csd-title">
            Custom Software Development Company in <span className="gradient-text">Delhi NCR &amp; India</span>
          </h1>
          <p className="csd-subtitle">
            We don't sell pre-packaged templates. We engineer bespoke custom software on-demand for clients — high-speed web apps, Android &amp; iOS mobile apps, custom ERPs, CRMs, and complex enterprise platforms tailored to your business rules.
          </p>
          <div style={{ marginTop: '2rem', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href={whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              Discuss Your Custom Project <ArrowRight size={16} />
            </a>
            <a href="/contact" className="btn btn-outline">
              Get Free Technical Proposal
            </a>
          </div>
        </motion.div>

        {/* Services Grid */}
        <div className="csd-grid">
          {services.map((item, idx) => (
            <motion.div 
              key={item.title}
              className="csd-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
            >
              <div className="csd-card-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* 6-Step Development Process */}
        <div style={{ margin: '80px 0' }}>
          <h2 className="text-center" style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '12px' }}>
            How Custom Software Development Is Done: <span className="gradient-text">Our 6-Step Process</span>
          </h2>
          <p className="text-center" style={{ color: '#a3a3a3', maxWidth: '700px', margin: '0 auto 40px', fontSize: '1rem', lineHeight: '1.6' }}>
            Here is our proven 6-stage engineering roadmap ensuring 100% transparency, code quality, and on-time project delivery.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {processStepsDetailed.map((step) => (
              <div key={step.num} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '28px' }}>
                <span style={{ fontSize: '2rem', fontWeight: '800', color: '#06b6d4' }}>{step.num}</span>
                <h4 style={{ fontSize: '1.2rem', fontWeight: '700', margin: '12px 0 8px', color: '#fff' }}>{step.title}</h4>
                <p style={{ color: '#a3a3a3', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tech Stack Matrix */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '40px', marginBottom: '80px' }}>
          <h3 style={{ fontSize: '1.8rem', fontWeight: '800', textAlign: 'center', marginBottom: '30px' }}>
            Modern Tech Stack &amp; Frameworks
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px' }}>
            {techStack.map((group) => (
              <div key={group.category}>
                <h4 style={{ color: '#06b6d4', fontSize: '1rem', fontWeight: '700', marginBottom: '12px' }}>{group.category}</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {group.tech.map((t) => (
                    <span key={t} style={{ color: '#d4d4d4', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CheckCircle size={14} color="#06b6d4" /> {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <FAQAccordion 
          title="Detailed Custom Software FAQs" 
          faqs={faqsDetailed} 
          accentColor="#06b6d4" 
        />

        {/* Bottom High-Converting Contact Banner */}
        <div style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.15) 0%, rgba(37,99,235,0.15) 100%)', border: '1px solid rgba(6,182,212,0.3)', borderRadius: '24px', padding: '48px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#fff', marginBottom: '12px' }}>Have a Custom Software Project in Mind?</h2>
          <p style={{ color: '#a3a3a3', maxWidth: '600px', margin: '0 auto 24px', fontSize: '1rem', lineHeight: '1.6' }}>
            Let's build something extraordinary together. Talk directly with our principal software architects for a free discovery call and milestone estimate.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={16} /> Chat on WhatsApp (+91 8287999236)
            </a>
            <a href="/contact" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <PhoneCall size={16} /> Fill Contact Form
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
